import React, { useMemo, useState } from "react";
import styled from "styled-components/macro";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import useSWR from "swr";
import type { Column } from "react-table";
import {
  PageHeader,
  PageTitle,
  PageWrapper,
} from "../../../../../layout/portalPageLayout";
import { Table } from "../../../../../components/Table/Table";
import { Pagination } from "../../../../../components/Pagination/Pagination";
import { SearchBar } from "../../../../../components/SearchBar/SearchBar";
import { endpoints } from "../../../../../endpoints";
import { useStoreState, TablePlaceholder } from "../../../../../util/util";
import { useRoutePath } from "../../../../../util/Routing";
import type {
  PaginatedReferenceEntities,
  ReferenceEntity,
} from "../../../../../types/types.PIM";
import { useDebounce } from "../../../../../util/hooks";
import { DelayedSpinner } from "../../../../../components/DelayedSpinner/DelayedSpinner";
import { ErrorPlaceholder } from "../../../../../components/Error";
import { ScrollTableWrapper } from "../../../../SharedPages/ProductDetailPage/Assets/DocumentsOrDigitalMediaTable/DocumentsOrDigitalMediaTable";
import { TableContainer } from "../../../../SharedPages/ProductDetailPage/Assets/util";

const SearchWrapper = styled.div`
  margin-bottom: ${({ theme }) => theme.resolve("spacing_3xl")};
  max-width: 500px;
`;

type ReferenceEntityTableRow = {
  id: string;
  display_name: string;
  description: string;
  column_count: number;
  row_count: number;
  created_at: string;
};

export function ReferenceEntitiesList(): JSX.Element {
  const { t } = useTranslation();
  const { storefront_id } = useStoreState();
  const { adminPath } = useRoutePath();
  const history = useHistory();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebounce(searchQuery, 500);
  const [offset, setOffset] = useState(0);
  const perPage = 50;

  const { data, error, isValidating } = useSWR<PaginatedReferenceEntities>(
    [
      endpoints.v2_storefronts_id_reference_entities(storefront_id),
      useMemo(
        () => ({
          params: {
            q: debouncedSearchQuery,
            limit: perPage,
            offset,
            order_by: "asc",
          },
        }),
        [debouncedSearchQuery, offset]
      ),
    ],
    {
      revalidateOnFocus: false,
    }
  );

  const columns: Column<ReferenceEntityTableRow>[] = useMemo(
    () => [
      {
        header: t("Name"),
        accessorKey: "display_name",
        id: "display_name",
      },
      {
        header: t("Description"),
        accessorKey: "description",
        id: "description",
        cell: ({ getValue }: any) => <>{getValue() || "—"}</>,
      },
      {
        header: t("Attributes"),
        accessorKey: "column_count",
        id: "column_count",
      },
      {
        header: t("Items"),
        accessorKey: "row_count",
        id: "row_count",
      },
      {
        header: t("Created"),
        accessorKey: "created_at",
        id: "created_at",
        cell: ({ getValue }: any) => {
          const value = getValue();
          if (!value) return <>—</>;
          const date = new Date(value);
          return (
            <>
              {date.toLocaleDateString(undefined, {
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </>
          );
        },
      },
    ],
    [t]
  );

  const tableData: ReferenceEntityTableRow[] = useMemo(() => {
    if (!data?.data) return [];
    return data.data.map((entity: ReferenceEntity) => ({
      id: entity.id,
      display_name: entity.display_name,
      description: entity.description,
      column_count: entity.column_count,
      row_count: entity.row_count,
      created_at: entity.created_at,
    }));
  }, [data]);

  const pagination = useMemo(() => {
    if (!data?.pagination) {
      return {
        perPage,
        pageCount: 0,
        pageIndex: 0,
      };
    }

    const { total, limit } = data.pagination;
    return {
      perPage: limit,
      pageCount: Math.ceil(total / limit),
      pageIndex: Math.floor(offset / limit),
    };
  }, [data?.pagination, offset]);

  const handlePageClick = (newOffset: number) => {
    setOffset(newOffset);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setOffset(0); // Reset to first page on search
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setOffset(0);
  };

  const handleRowClick = (e: React.MouseEvent<HTMLTableRowElement>) => {
    const referenceEntityId = e.currentTarget.id;
    if (referenceEntityId) {
      history.push(`${adminPath}/pim/reference-entities/${referenceEntityId}`);
    }
  };

  const isLoading = isValidating && !data;

  if (error && !data) {
    return (
      <PageWrapper>
        <PageHeader>
          <PageTitle>{t("Reference Entities")}</PageTitle>
        </PageHeader>
        <div>
          <ErrorPlaceholder
            message={t("There was an error loading reference entities.")}
          />
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <PageHeader>
        <PageTitle>{t("Reference Entities")}</PageTitle>
      </PageHeader>
      <div>
        <SearchWrapper>
          <SearchBar
            query={searchQuery}
            placeHolder={t("Search reference entities...")}
            handleChange={handleSearch}
            handleClearInput={handleClearSearch}
          />
        </SearchWrapper>

        {isLoading && <DelayedSpinner />}

        {!isLoading && (
          <>
            <TableContainer>
              <ScrollTableWrapper
                maxHeight={
                  pagination.pageCount > 1
                    ? "calc(100vh - 320px)"
                    : "calc(100vh - 280px)"
                }
              >
                <Table
                  columns={columns}
                  data={tableData}
                  isLoading={isLoading}
                  error={error}
                  rowClick={handleRowClick}
                  Placeholder={
                    <TablePlaceholder
                      message={t("No reference entities found")}
                    />
                  }
                />
              </ScrollTableWrapper>
            </TableContainer>
            <Pagination
              pagination={pagination}
              offset={offset}
              handlePageClick={handlePageClick}
            />
          </>
        )}
      </div>
    </PageWrapper>
  );
}
